import { DragControls } from "framer-motion";
import { useCallback } from "react";
import { MdDragIndicator, MdOutlinePushPin, MdClose } from "react-icons/md";

interface InstructionsPanelHeaderProps {
  isDraggable: boolean;
  isDragging: boolean;
  setIsDraggable: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
  handleClosePanel: () => void;
  controls: DragControls;
}

const InstructionsPanelHeader: React.FC<InstructionsPanelHeaderProps> = ({
  isDraggable,
  isDragging,
  setIsDraggable,
  setIsDragging,
  handleClosePanel,
  controls,
}) => {
  /**
   * Function used to set up the parameters used for the dragging
   * process
   */
  const handleDragMouseDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isDraggable) {
      controls.start(event); // Initiates drag
    }

    // Prevent default behaviour
    event.preventDefault();
    // Change the cursor
    document.body.style.cursor = "grabbing";
    // And set the drag state
    setIsDragging(true);

    // Add a listener to change this when pressing mouse up
    window.addEventListener("pointerup", handleDragMouseUp);
    return () => window.removeEventListener("pointerup", handleDragMouseUp);
  };

  /**
   * Function used to turn off the parameters that are activated
   * during the dragging process
   */
  const handleDragMouseUp = useCallback(() => {
    // Set dragging to false
    setIsDragging(false);
    // And reset the cursor
    document.body.style.cursor = "default";

    window.removeEventListener("pointerup", handleDragMouseUp);
  }, [document]);

  return (
    <div className="h-9 justify-between items-center gap-2 inline-flex">
      <div className="text-neutral-700 text-[23px] font-semibold font-poppins leading-7 select-none">
        Instructions
      </div>
      <div className="self-stretch inline-flex justify-center items-center">
        <div
          className={`w-8 h-8 inline-flex justify-center items-center relative touch-none ${
            isDragging ? "cursor-grabbing" : "cursor-grab"
          } -rotate-90${
            isDraggable ? " hover:bg-neutral-25 rounded-full" : ""
          } `}
          onPointerDown={handleDragMouseDown}
        >
          <MdDragIndicator
            className={`w-6 h-6 ${
              isDraggable ? "fill-neutral-700" : "fill-Subtle"
            }`}
          />
        </div>
      </div>
      <div className="self-stretch justify-end items-center gap-2 inline-flex">
        <div
          className="p-2 bg-neutral-100 hover:bg-neutral-200 ease-in-out duration-150 rounded-full justify-center items-center gap-2 flex cursor-pointer"
          onClick={() => setIsDraggable(!isDraggable)}
        >
          <div className="w-5 h-5 relative">
            <MdOutlinePushPin
              className={`w-full h-full ${
                isDraggable ? "fill-neutral-700" : "fill-Subtle"
              }`}
            />
          </div>
        </div>
        <div
          className="p-2 bg-neutral-100 hover:bg-neutral-200 ease-in-out duration-150 rounded-full justify-center items-center gap-2 flex cursor-pointer"
          onClick={handleClosePanel}
        >
          <div className="w-5 h-5 relative">
            <MdClose className="w-full h-full fill-neutral-700" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstructionsPanelHeader;
